import type { Theme } from '@mui/material/styles';

/**
 * Theme-aware row-highlight colors. The tables originally hard-coded pale hex
 * values (e.g. `#fff8e1`) that stayed light in dark mode, leaving light text
 * unreadable on a light row. Each helper returns a translucent tint on dark and
 * the original pale color on light, so it can be dropped straight into an MUI
 * `sx` value (which resolves `(theme) => …` callbacks).
 */

const isDark = (theme: Theme) => theme.palette.mode === 'dark';

/** Walk-in / anonymous row highlight (amber). */
export const walkInRowBg = (theme: Theme) =>
  isDark(theme) ? 'rgba(255, 213, 79, 0.16)' : '#fff8e1';

/** Selected / focused row highlight (cyan). */
export const selectedRowBg = (theme: Theme) =>
  isDark(theme) ? 'rgba(77, 208, 225, 0.22)' : '#e0f7fa';

/** Expired / warning row highlight (peach). */
export const expiredRowBg = (theme: Theme) =>
  isDark(theme) ? 'rgba(255, 167, 38, 0.2)' : '#FFE2B8';

/** Neutral row hover. */
export const rowHoverBg = (theme: Theme) =>
  isDark(theme) ? 'rgba(255, 255, 255, 0.06)' : '#f5f5f5';

/** Subtle input/field background (used on some highlighted form fields). */
export const subtleFieldBg = (theme: Theme) =>
  isDark(theme) ? 'rgba(255, 255, 255, 0.06)' : '#f9fafb';

// Opaque variants for STICKY cells (e.g. the pinned Actions column). A sticky
// cell must be fully opaque or the horizontally-scrolled columns bleed through
// it; the translucent row tints above cannot be used there. These composite the
// same tint over the dark paper surface so they still read as the highlight.

/** Opaque walk-in / anonymous highlight for sticky cells. */
export const walkInRowBgOpaque = (theme: Theme) =>
  isDark(theme) ? '#3b3d32' : '#fff8e1';

/** Opaque selected / focused highlight for sticky cells. */
export const selectedRowBgOpaque = (theme: Theme) =>
  isDark(theme) ? '#224754' : '#e0f7fa';
